import React, { useState } from 'react';
import type { TransactionItem } from '../TransaccionesPageNew';

interface TransactionsTableProps {
    transactions: TransactionItem[];
    isLoading: boolean;
    onOpenDrawer: (tx: TransactionItem) => void;
}

export const TransaccionesTable: React.FC<TransactionsTableProps> = ({ transactions, isLoading, onOpenDrawer }) => {
    const [selectedId, setSelectedId] = useState<number | null>(null);

    const handleRowClick = (tx: TransactionItem) => {
        setSelectedId(tx.payoutTransactionId);
        onOpenDrawer(tx); 
    };

    const formatMoney = (val: number) => `Bs. ${val.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

    return (
        <div className="flex flex-col flex-1 w-full min-h-[400px] bg-white">
            <div className="w-full h-full overflow-auto hide-scrollbar">
                <table className="w-full min-w-[900px] border-collapse">
                    <thead className="sticky top-0 z-10 bg-gray-50 border-y border-gray-200 shadow-sm">
                        <tr>
                            <th className="px-4 sm:px-7 py-3 text-left text-[10px] font-bold tracking-[0.8px] text-gray-500 uppercase whitespace-nowrap select-none">Código ↕</th>
                            <th className="px-4 sm:px-7 py-3 text-left text-[10px] font-bold tracking-[0.8px] text-gray-500 uppercase whitespace-nowrap select-none">Fecha ↕</th>
                            <th className="px-4 sm:px-7 py-3 text-left text-[10px] font-bold tracking-[0.8px] text-gray-500 uppercase whitespace-nowrap">Huésped</th>
                            <th className="px-4 sm:px-7 py-3 text-left text-[10px] font-bold tracking-[0.8px] text-gray-500 uppercase whitespace-nowrap">Propiedad</th>
                            <th className="px-4 sm:px-7 py-3 text-left text-[10px] font-bold tracking-[0.8px] text-gray-500 uppercase whitespace-nowrap select-none">Monto ↕</th>
                            <th className="px-4 sm:px-7 py-3 text-left text-[10px] font-bold tracking-[0.8px] text-gray-500 uppercase whitespace-nowrap">Comisión</th>
                            <th className="px-4 sm:px-7 py-3 text-left text-[10px] font-bold tracking-[0.8px] text-gray-500 uppercase whitespace-nowrap">Estado</th>
                            <th className="px-4 sm:px-7 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">Cargando transacciones...</td>
                            </tr>
                        ) : transactions.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">No hay transacciones en este periodo.</td>
                            </tr>
                        ) : (
                            transactions.map((tx) => {
                                const dateObj = new Date(tx.createdAt);
                                const dateStr = dateObj.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
                                const timeStr = dateObj.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

                                return (
                                    <tr 
                                        key={tx.payoutTransactionId} 
                                        className={`cursor-pointer transition-colors border-l-2 ${
                                            selectedId === tx.payoutTransactionId 
                                                ? 'bg-indigo-50/50 border-[#4f6df5]' 
                                                : 'hover:bg-gray-50 border-transparent'
                                        }`}
                                        onClick={() => handleRowClick(tx)}
                                    >
                                        <td className="px-4 sm:px-7 py-3.5 text-[13px] border-b border-gray-100 align-middle">
                                            <span className="text-[11px] font-mono text-[#4f6df5]">#{tx.transactionCode}</span>
                                        </td>
                                        <td className="px-4 sm:px-7 py-3.5 text-[12px] text-gray-500 border-b border-gray-100 align-middle">
                                            {dateStr}<br/>
                                            <span className="text-[10px] opacity-80">{timeStr}</span>
                                        </td>
                                        <td className="px-4 sm:px-7 py-3.5 border-b border-gray-100 align-middle">
                                            <div className="flex items-center gap-2.5">
                                                {tx.guest.avatarColor ? (
                                                    <img src={tx.guest.avatarColor} alt={tx.guest.initials} className="w-7 h-7 rounded-full object-cover bg-gray-200 shrink-0" />
                                                ) : (
                                                    <div className="flex items-center justify-center shrink-0 w-7 h-7 text-[10px] font-bold text-white rounded-full bg-gradient-to-br from-indigo-500 to-purple-500">
                                                        {tx.guest.initials}
                                                    </div>
                                                )}
                                                <div className="text-[13px] font-medium text-gray-900 whitespace-nowrap">{tx.guest.fullName}</div>
                                            </div>
                                        </td>
                                        <td className="px-4 sm:px-7 py-3.5 text-[12px] text-gray-500 border-b border-gray-100 align-middle whitespace-nowrap">
                                            {tx.propertyName}
                                        </td>
                                        <td className="px-4 sm:px-7 py-3.5 border-b border-gray-100 align-middle">
                                            <span className={`font-mono font-bold ${tx.amount > 0 ? 'text-emerald-600' : 'text-gray-900'}`}>
                                                {formatMoney(tx.amount)}
                                            </span>
                                        </td>
                                        <td className="px-4 sm:px-7 py-3.5 text-[12px] border-b border-gray-100 align-middle text-amber-500">
                                            {formatMoney(tx.commission)}
                                        </td>
                                        <td className="px-4 sm:px-7 py-3.5 border-b border-gray-100 align-middle">
                                            <span className="px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase rounded-full whitespace-nowrap bg-emerald-50 text-emerald-600 border border-emerald-100">
                                                {tx.transactionStatus}
                                            </span>
                                        </td>
                                        <td className="px-4 sm:px-7 py-3.5 border-b border-gray-100 align-middle">
                                            <button 
                                                className="px-2.5 py-1 text-[11px] transition-colors bg-white border rounded-md border-gray-200 text-gray-500 hover:border-[#4f6df5] hover:text-[#4f6df5]"
                                                onClick={(e) => {
                                                    e.stopPropagation(); 
                                                    handleRowClick(tx);
                                                }}
                                            >
                                                Ver
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TransaccionesTable;